import { Alert, Box, Button, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { AskContainer } from "../Components";
import { INFO_TYPE } from "Common/Constants";
import { DocumentOutlined, ServiceInformationOutlined } from "Common/Assets";
import { CUSTOM_RADII } from "Theme/Shape";
import { useContext } from "react";
import { AuthContext } from "Layout/MainLayout";

interface IInitialChat {
  sendRequestMessage: (requestMessage: string) => void;
  infoType: INFO_TYPE;
  setInfoType: (info: INFO_TYPE) => void;
}

const infoAlertSx: SxProps<Theme> = {
  borderRadius: CUSTOM_RADII.subtle,
  alignItems: "center",
};

export const InitialChat = (props: IInitialChat) => {
  const { sendRequestMessage, infoType, setInfoType } = props;
  const theme = useTheme();
  const MatchDownMd = useMediaQuery(theme.breakpoints.down("md"));
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("Component must be used within an AuthProvider");
  }
  const { authDetails } = context;
  const hasServiceHistory = authDetails?.features?.includes("service_intelligence");

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ gap: 5, width: "100%" }}>
        <Typography variant={"h5"} sx={{ textAlign: "center" }}>
          How may I assist you?
        </Typography>
        {hasServiceHistory && (
          <Stack
            spacing={2}
            direction={MatchDownMd ? "column" : "row"}
            sx={{
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
              py: 3,
            }}
          >
            <Button
              variant={infoType === INFO_TYPE.DOCUMENT ? "contained" : "outlined"}
              sx={{ px: 4 }}
              startIcon={<DocumentOutlined />}
              onClick={() => setInfoType(INFO_TYPE.DOCUMENT)}
            >
              Document
            </Button>
            <Button
              variant={infoType === INFO_TYPE.SERVICE_HISTORY ? "contained" : "outlined"}
              sx={{ px: 4 }}
              startIcon={<ServiceInformationOutlined />}
              onClick={() => setInfoType(INFO_TYPE.SERVICE_HISTORY)}
            >
              Service Intelligence
            </Button>
          </Stack>
        )}

        {infoType === INFO_TYPE.SERVICE_HISTORY && (
          <Box sx={{ display: "flex", justifyContent: "center", pb: 2 }}>
            <Alert severity="info" sx={infoAlertSx}>
              Enter the serial number to get service information. {MatchDownMd && <br />}
              <strong> Example</strong>: Tell me service history for{" "}
              <em>
                <strong>{"{serial_number}"}</strong>
              </em>{" "}
              from{" "}
              <em>
                <strong>{"{brand_name}"}</strong>
              </em>
            </Alert>
          </Box>
        )}
        {infoType === INFO_TYPE.DOCUMENT && (
          <Box sx={{ display: "flex", justifyContent: "center", pb: 2, px: 2 }}>
            <Alert severity="info" sx={infoAlertSx}>
              For best results, please make sure to include the <strong>brand name</strong> and{" "}
              <strong>specific details like model number</strong>, etc.
            </Alert>
          </Box>
        )}
        <AskContainer sendRequestMessage={sendRequestMessage} />
      </Box>
    </Box>
  );
};
